/**
 * src/services/spatial/mapboxProvider.ts
 *
 * Concrete `SpatialMapProvider` implementation for `mapbox-gl-js`.
 *
 * This provider WRAPS an existing `mapboxgl.Map` instance — it never
 * constructs one. App.tsx already builds the main Mapbox map with all
 * its existing layers (countries, agents, NFTs, contracts, telemetry,
 * teleport, …) and the spatial-objects subsystem layers on top via
 * this wrapper. Keeping the lifecycle in App.tsx is what ensures the
 * existing Mapbox map keeps working unchanged.
 *
 * The provider tracks every listener it adds so `dispose()` can detach
 * cleanly when the SpatialObjectsMapLayer component unmounts (e.g.
 * when the user toggles spatial objects off).
 */

import type mapboxgl from 'mapbox-gl';
import type {
  ProviderEventHandler,
  ProviderGeoJsonData,
  ProviderInteractiveEvent,
  ProviderLayerSpec,
  ProviderRasterSourceSpec,
  ProviderSourceSpec,
  SpatialMapProvider,
} from './mapProvider';
import type { Geometry, Position } from './geoLinkTypes';

interface BoundListener {
  event: ProviderInteractiveEvent;
  layerId: string;
  external: ProviderEventHandler;
  internal: (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => void;
}

class MapboxProvider implements SpatialMapProvider {
  readonly id = 'mapbox' as const;
  readonly displayName = 'Mapbox GL JS';
  readonly nativeMap: mapboxgl.Map;

  private boundListeners: BoundListener[] = [];

  constructor(nativeMap: mapboxgl.Map) {
    if (!nativeMap || typeof (nativeMap as unknown as { addLayer?: unknown }).addLayer !== 'function') {
      throw new Error('[mapboxProvider] expected a mapboxgl.Map instance');
    }
    this.nativeMap = nativeMap;
  }

  isStyleLoaded(): boolean {
    try {
      return !!this.nativeMap.isStyleLoaded?.();
    } catch {
      return false;
    }
  }

  whenReady(cb: () => void): void {
    if (this.isStyleLoaded()) {
      cb();
      return;
    }
    const handler = () => {
      // Style swaps fire 'styledata' multiple times — we only fire once
      // the style is fully loaded.
      if (this.isStyleLoaded()) {
        this.nativeMap.off('styledata', handler);
        this.nativeMap.off('load', handler);
        cb();
      }
    };
    this.nativeMap.on('styledata', handler);
    this.nativeMap.on('load', handler);
  }

  hasLayer(id: string): boolean {
    try { return !!this.nativeMap.getLayer(id); } catch { return false; }
  }

  hasSource(id: string): boolean {
    try { return !!this.nativeMap.getSource(id); } catch { return false; }
  }

  addSource(id: string, spec: ProviderSourceSpec): void {
    if (this.hasSource(id)) return;
    // mapbox-gl's typings exposed `GeoJSONSourceOptions` in some
    // releases and renamed it in others — using the AnySourceData
    // shape via cast keeps us forwards / backwards compatible without
    // a hard dep on a specific minor version.
    //
    // Strip any undefined fields. Mapbox's source-spec validator has
    // historically rejected sources that ship with a `tolerance:
    // undefined` (or any other typed-numeric field set to undefined)
    // by emitting a silent `error` event instead of throwing — which
    // the caller then perceives as `addSource()` succeeding but
    // `getSource()` returning falsy on the very next tick. Build the
    // payload incrementally to avoid setting any undefined keys.
    const payload: Record<string, unknown> = {
      type: 'geojson',
      data: spec.data,
    };
    if (spec.generateId !== false) payload.generateId = spec.generateId ?? true;
    if (typeof spec.tolerance === 'number') payload.tolerance = spec.tolerance;
    this.nativeMap.addSource(id, payload as unknown as mapboxgl.AnySourceData);
  }

  addRasterSource(id: string, spec: ProviderRasterSourceSpec): void {
    if (this.hasSource(id)) return;
    const payload: Record<string, unknown> = {
      type: 'raster',
      tiles: spec.tiles,
      tileSize: spec.tileSize ?? 256,
    };
    if (spec.attribution) payload.attribution = spec.attribution;
    if (typeof spec.minzoom === 'number') payload.minzoom = spec.minzoom;
    if (typeof spec.maxzoom === 'number') payload.maxzoom = spec.maxzoom;
    this.nativeMap.addSource(id, payload as unknown as mapboxgl.AnySourceData);
  }

  setSourceData(id: string, data: ProviderGeoJsonData): void {
    const src = this.nativeMap.getSource(id) as mapboxgl.GeoJSONSource | undefined;
    if (!src || typeof src.setData !== 'function') return;
    src.setData(data as unknown as GeoJSON.FeatureCollection);
  }

  removeSource(id: string): void {
    if (!this.hasSource(id)) return;
    try { this.nativeMap.removeSource(id); } catch { /* ignore */ }
  }

  addLayer(spec: ProviderLayerSpec, beforeId?: string): void {
    if (this.hasLayer(spec.id)) return;
    // Pick an insertion point that stays under any symbol/text layers
    // so labels (country names, place names) keep painting on top.
    let resolvedBefore = beforeId;
    if (!resolvedBefore) {
      try {
        const labelLayer = this.nativeMap.getStyle?.()?.layers?.find(
          (l: mapboxgl.AnyLayer) => l.type === 'symbol' && (l.layout as { 'text-field'?: unknown } | undefined)?.['text-field'],
        );
        resolvedBefore = labelLayer?.id;
      } catch { /* style may not be ready yet */ }
    }
    // Mapbox 3.x's strict style-spec validator rejects layers that
    // ship with `layout: undefined`, `minzoom: undefined`, etc. via a
    // silent `error` event (no thrown exception). The result is that
    // `addLayer` returns successfully but `getLayer` reports the layer
    // missing — the exact symptom that left the main map blank.
    // Build the spec incrementally so we never set any undefined keys.
    const payload: Record<string, unknown> = {
      id: spec.id,
      type: spec.type,
      source: spec.source,
      paint: spec.paint ?? {},
      layout: spec.layout ?? {},
    };
    if (spec.filter !== undefined && spec.filter !== null) payload.filter = spec.filter;
    if (typeof spec.minzoom === 'number') payload.minzoom = spec.minzoom;
    if (typeof spec.maxzoom === 'number') payload.maxzoom = spec.maxzoom;
    this.nativeMap.addLayer(
      payload as unknown as mapboxgl.AnyLayer,
      resolvedBefore,
    );
  }

  removeLayer(id: string): void {
    if (!this.hasLayer(id)) return;
    try { this.nativeMap.removeLayer(id); } catch { /* ignore */ }
  }

  setFilter(layerId: string, filter: unknown[] | null): void {
    if (!this.hasLayer(layerId)) return;
    this.nativeMap.setFilter(layerId, filter as mapboxgl.Expression | null);
  }

  setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.hasLayer(layerId)) return;
    this.nativeMap.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }

  setPaintProperty(layerId: string, name: string, value: unknown): void {
    if (!this.hasLayer(layerId)) return;
    // mapbox-gl's typings enumerate every paint key per layer type;
    // since we accept any string here, cast through `unknown` to keep
    // the abstraction provider-agnostic.
    (this.nativeMap.setPaintProperty as unknown as (
      l: string, n: string, v: unknown,
    ) => void)(layerId, name, value);
  }

  setLayoutProperty(layerId: string, name: string, value: unknown): void {
    if (!this.hasLayer(layerId)) return;
    (this.nativeMap.setLayoutProperty as unknown as (
      l: string, n: string, v: unknown,
    ) => void)(layerId, name, value);
  }

  on(event: ProviderInteractiveEvent, layerId: string, handler: ProviderEventHandler): void {
    const internal = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      handler({
        type: event,
        point: { x: e.point.x, y: e.point.y },
        lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
        features: (e.features ?? []).map(f => ({
          id: f.id,
          properties: (f.properties ?? {}) as Record<string, unknown>,
          geometry: f.geometry as Geometry,
          layer: f.layer ? { id: f.layer.id } : undefined,
        })),
        originalEvent: e.originalEvent,
      });
    };
    this.nativeMap.on(event, layerId, internal as never);
    this.boundListeners.push({ event, layerId, external: handler, internal });
  }

  off(event: ProviderInteractiveEvent, layerId: string, handler: ProviderEventHandler): void {
    const idx = this.boundListeners.findIndex(
      l => l.event === event && l.layerId === layerId && l.external === handler,
    );
    if (idx === -1) return;
    const [bound] = this.boundListeners.splice(idx, 1);
    try { this.nativeMap.off(event, layerId, bound.internal as never); } catch { /* ignore */ }
  }

  queryFeaturesAtPoint(
    point: { x: number; y: number },
    layerIds: string[],
  ): Array<{ id?: string | number; properties: Record<string, unknown>; geometry: Geometry; layer?: { id: string } }> {
    const live = layerIds.filter(id => this.hasLayer(id));
    if (live.length === 0) return [];
    const features = this.nativeMap.queryRenderedFeatures([point.x, point.y] as mapboxgl.PointLike, {
      layers: live,
    });
    return features.map(f => ({
      id: f.id,
      properties: (f.properties ?? {}) as Record<string, unknown>,
      geometry: f.geometry as Geometry,
      layer: f.layer ? { id: f.layer.id } : undefined,
    }));
  }

  getBounds() {
    const b = this.nativeMap.getBounds();
    if (!b) {
      // mapbox-gl can return null pre-load — fall back to "world".
      return { sw: { lng: -180, lat: -85 }, ne: { lng: 180, lat: 85 } };
    }
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    return { sw: { lng: sw.lng, lat: sw.lat }, ne: { lng: ne.lng, lat: ne.lat } };
  }

  getZoom(): number {
    return this.nativeMap.getZoom?.() ?? 0;
  }

  getCenter(): { lng: number; lat: number } {
    const c = this.nativeMap.getCenter();
    return { lng: c.lng, lat: c.lat };
  }

  flyTo(opts: { center?: Position; zoom?: number; pitch?: number; bearing?: number; speed?: number }): void {
    this.nativeMap.flyTo({
      center: opts.center as mapboxgl.LngLatLike | undefined,
      zoom: opts.zoom,
      pitch: opts.pitch,
      bearing: opts.bearing,
      speed: opts.speed,
    });
  }

  dispose(): void {
    while (this.boundListeners.length > 0) {
      const l = this.boundListeners.pop();
      if (!l) continue;
      try { this.nativeMap.off(l.event, l.layerId, l.internal as never); } catch { /* ignore */ }
    }
    // Intentionally do NOT remove() the underlying map — App.tsx owns it.
  }
}

export function createMapboxProvider(nativeMap: unknown): SpatialMapProvider {
  return new MapboxProvider(nativeMap as mapboxgl.Map);
}
